import { Address, DeliveryDateType, DocumentType } from '@synergy/models';

import { Contact } from '../../../common/models/contact';
import { ConverterJob } from '../models/internal/converterJob';
import { ConverterPositionItem } from '../models/internal/converter-positionItem';
import { Demand } from '../../demands/models/demand';
import { DemandAccount } from '../../demands/models/demand-account';
import { DemandAccountItem } from '../../demands/models/demand-account-item';
import { DemandAccountStatus } from '../../demands/models/demand-account-status';
import { TransitionTarget } from '../models/internal/transition-target';

interface DemandGroup {
    address: Address;
    contact: Contact;
    name: string;
    isLocked?: boolean;
    items: DemandAccountItem[];
}

export const createConverterJobFromDemand = (demand: Demand): ConverterJob | undefined => {
    const groups: DemandGroup[] = [];
    demand.demandAccounts
        .filter(account =>
            [DemandAccountStatus.Accepted, DemandAccountStatus.PartiallyAccepted].includes(account.status)
        )
        .forEach((demandAccount: DemandAccount) => {
            demandAccount.demandAccountItems.forEach((item: DemandAccountItem) => {
                if (item.noDemand) {
                    return;
                }

                const existingGroup = groups.find(
                    group =>
                        group.address?.id === item.deliveryAddress.id &&
                        group.contact?.id === demandAccount.customerContact.id
                );
                if (existingGroup) {
                    existingGroup.items.push(item);
                    return;
                }

                groups.push({
                    address: item.deliveryAddress,
                    contact: demandAccount.customerContact,
                    name: demandAccount.customerAccount.name,
                    isLocked: demandAccount.customerAccount.isLocked,
                    items: [item],
                });
            });
        });

    const customers = groups.map(group => ({
        id: group.address.id,
        name: group.name,
        isLocked: group.isLocked,
        deliveryAddress: group.address,
        customerContact: group.contact,
        customerSuppliers: [
            {
                id: 0,
                name: '',
                items: group.items.map(ConverterPositionItem.fromPlainObject),
                deliveryPrice: 0,
                surcharge: 0,
            },
        ],
    }));

    const job: ConverterJob = {
        source: demand,
        possibleTargets: [TransitionTarget.PurchaseOrder, TransitionTarget.Inquiry],
        selectedTarget: TransitionTarget.Inquiry,
        selectedCustomerId: -1,
        documentType: DocumentType.Demand,
        supportsDeliveryCosts: true,
        customers,
        hasDeliveryDate: true,
        deliveryDateType: DeliveryDateType.Deadline,
        // A survey without dates has no period to name; building the object
        // anyway printed the word "undefined" into the dialog's headline.
        ...(demand.deliveryDateFrom && demand.deliveryDateTo
            ? { deliveryDateRange: { from: demand.deliveryDateFrom, to: demand.deliveryDateTo } }
            : {}),
    };

    return ConverterJob.fromPlainObject(job);
};
